'use client';

// ============================================================================
// LES PORTES DES UNIVERS : « vers ou voulez-vous aller »
// ----------------------------------------------------------------------------
// Sur la porte commune (app.vyndra.fr), chaque univers de l'ecosysteme a sa
// porte : sa marque, son adresse, et un clic qui y mene. Vyndra y figure aussi,
// marque « vous etes ici » : c'est la porte que l'on a sous les yeux.
//
// D'OU VIENNENT LES PORTES. De GET /hubs/public/liste, lu par le composant
// serveur : nom de marque, couleur, logos, adresse. Rien n'est ecrit ici. Un
// univers ajoute demain dans les reglages a sa porte sans toucher au code.
//
// OU ELLES MENENT. A l'ecran de connexion de l'univers, sur SA propre adresse
// (adressePorte, ecosysteme.ts). L'ancien ecran ecrivait `https://${hote}` avec
// l'etiquette seule du sous-domaine : ses deux tuiles menaient a une adresse qui
// n'existe pas.
//
// LE GESTE. C'est celui du changement d'espace dans l'application : deux
// battants a la couleur de la marque se referment le long d'une fente qui passe
// par le point touche, le sceau de la marque apparait, puis la navigation part.
// L'ecran d'arrivee rouvre les battants au meme point (ArriveePorte). Garde-fous :
//   - c'est un vrai lien : clic du milieu, Ctrl+clic et « ouvrir dans un nouvel
//     onglet » marchent sans rien jouer ;
//   - `prefers-reduced-motion` : navigation immediate, aucun battant ;
//   - la navigation part quoi qu'il arrive au bout de la course, et un retour
//     arriere (cache de page du navigateur) retire les battants.
// ============================================================================

import { useEffect, useState, type CSSProperties, type MouseEvent } from 'react';
import { ArrowUpRight } from '@phosphor-icons/react';
import { BrandLogo, lireReglagesEmplacements, type JeuLogos } from '@/components/ui/brand-asset';
import { construirePalette, variablesEspace } from '@/components/espace/couleurs';
import { geometriePorte } from '@/components/espace/espace-metamorphose';
import { FiligraneMarque } from '@/components/espace/marque-typographie';
import { VyndraLogo } from '@/components/vyndra-logo';
import { avecGestePorte, lireGestePorte } from './ecosysteme';
import s from './connexion.module.css';

/** Une porte, telle que le composant serveur l'a preparee. */
export interface PorteUnivers {
  slug: string;
  /** Nom de la MARQUE (« Guard Informatique »), repli sur le libelle de l'univers. */
  nom: string;
  couleur: string | null;
  couleurSecondaire: string | null;
  jeu: JeuLogos;
  logoEmplacements: unknown;
  /** Adresse de l'ecran de connexion de l'univers. */
  href: string;
  /** Ce qu'on affiche de l'adresse : l'hote seul (« guardinformatique.vyndra.fr »). */
  affichee: string;
}

/** Duree de la fermeture des battants, alignee sur la feuille de style (0,5 s). */
const DUREE_DEPART_MS = 540;

function mouvementReduit(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface Depart {
  porte: PorteUnivers;
  x: number;
  y: number;
}

/** Les deux battants et le sceau, positionnes sur la fente qui passe par (x, y). */
function Battants({
  porte,
  x,
  y,
  sens,
}: {
  porte: PorteUnivers;
  x: number;
  y: number;
  sens: 'depart' | 'arrivee';
}) {
  const g = geometriePorte(x, y, window.innerWidth, window.innerHeight);
  const palette = construirePalette(porte.couleur, porte.couleurSecondaire);
  return (
    <div
      className={`${s.battants} ${sens === 'depart' ? s.battantsDepart : s.battantsArrivee}`}
      aria-hidden="true"
      style={
        {
          ...variablesEspace(palette),
          '--x': `${g.x}px`,
          '--y': `${g.y}px`,
          '--lw': `${g.largeur}px`,
          '--hh': `${g.hauteurHaut}px`,
          '--hb': `${g.hauteurBas}px`,
        } as CSSProperties
      }
    >
      <div className={`${s.battant} ${s.battantHaut}`} />
      <div className={`${s.battant} ${s.battantBas}`} />
      <FiligraneMarque
        jeu={porte.jeu}
        reglagesEmplacements={lireReglagesEmplacements(porte.logoEmplacements)}
        className={s.sceau}
        taille="min(40vmin, 380px)"
        opacite={1}
        couleur="var(--acc-on, #FFFFFF)"
        cadrage="signe"
      />
    </div>
  );
}

export function PortesUnivers({
  portes,
  hoteCourant,
  onViser,
}: {
  portes: PorteUnivers[];
  /** L'adresse de la porte commune, affichee sous « Vyndra ». */
  hoteCourant: string | null;
  /** Survol ou focus d'une porte : le prisme allume ses losanges. */
  onViser: (slug: string | null) => void;
}) {
  const [depart, setDepart] = useState<Depart | null>(null);

  // Retour arriere depuis l'univers : le navigateur peut restaurer CETTE page
  // telle qu'on l'a quittee, battants fermes. On les retire.
  useEffect(() => {
    const auRetour = (e: PageTransitionEvent) => {
      if (e.persisted) setDepart(null);
    };
    window.addEventListener('pageshow', auRetour);
    return () => window.removeEventListener('pageshow', auRetour);
  }, []);

  const partir = (e: MouseEvent<HTMLAnchorElement>, porte: PorteUnivers) => {
    // Un clic modifie (nouvel onglet, nouvelle fenetre) garde le comportement
    // normal du lien : rien a jouer, la page courante reste.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const cadre = e.currentTarget.getBoundingClientRect();
    const x = e.clientX || cadre.left + cadre.width / 2;
    const y = e.clientY || cadre.top + cadre.height / 2;
    if (mouvementReduit()) return;
    e.preventDefault();
    const adresse = avecGestePorte(porte.href, x, y, window.innerWidth, window.innerHeight);
    setDepart({ porte, x, y });
    window.setTimeout(() => window.location.assign(adresse), DUREE_DEPART_MS);
  };

  return (
    <section className={s.portes} aria-labelledby="portes-titre">
      <h2 id="portes-titre" className={s.portesTitre}>Où allez-vous ?</h2>
      <p className={s.portesSous}>
        Chaque univers a sa propre porte d&apos;entrée, à son adresse et à ses couleurs.
      </p>
      <ul className={s.portesListe}>
        <li>
          {/* La porte commune : celle que l'on a sous les yeux. Pas un lien,
              un repere. Elle allume la clef de voute du prisme. */}
          <div
            className={`${s.porte} ${s.porteIci}`}
            aria-current="page"
            onMouseEnter={() => onViser('vyndra')}
            onMouseLeave={() => onViser(null)}
          >
            <span className={s.porteLogo} aria-hidden="true">
              <VyndraLogo size={30} />
            </span>
            <span className={s.porteTexte}>
              <span className={s.porteNom}>
                <span className={s.porteNomTexte}>Vyndra</span>
                <span className={s.porteIciPastille}>Vous êtes ici</span>
              </span>
              {hoteCourant && <span className={s.porteAdresse}>{hoteCourant}</span>}
            </span>
          </div>
        </li>
        {portes.map((porte) => {
          const palette = construirePalette(porte.couleur, porte.couleurSecondaire);
          return (
            <li key={porte.slug}>
              <a
                href={porte.href}
                className={s.porte}
                style={variablesEspace(palette)}
                aria-label={`Entrer dans ${porte.nom}, ${porte.affichee}`}
                onMouseEnter={() => onViser(porte.slug)}
                onMouseLeave={() => onViser(null)}
                onFocus={() => onViser(porte.slug)}
                onBlur={() => onViser(null)}
                onClick={(e) => partir(e, porte)}
              >
                <span className={s.porteLogo} aria-hidden="true">
                  <BrandLogo
                    jeu={porte.jeu}
                    emplacement="lanceur_carte"
                    reglagesEmplacements={lireReglagesEmplacements(porte.logoEmplacements)}
                    nom={porte.nom}
                    repli="rien"
                    imgClassName={s.porteLogoImage}
                    className={s.porteLogoRepli}
                  />
                </span>
                <span className={s.porteTexte}>
                  <span className={s.porteNom}>
                    <span className={s.porteNomTexte}>{porte.nom}</span>
                  </span>
                  <span className={s.porteAdresse}>{porte.affichee}</span>
                </span>
                <ArrowUpRight size={18} className={`${s.ico} ${s.porteFleche}`} aria-hidden="true" />
              </a>
            </li>
          );
        })}
      </ul>
      {depart && <Battants porte={depart.porte} x={depart.x} y={depart.y} sens="depart" />}
    </section>
  );
}

/**
 * L'ARRIVEE PAR UNE PORTE, sur l'ecran de connexion d'un univers.
 *
 * Le serveur a vu `?porte=` dans l'adresse et a deja peint un voile a la
 * couleur de la marque (pas d'eclair sombre entre les deux pages). Au montage,
 * les battants prennent sa place, fermes, au point du geste, puis s'ouvrent.
 * Le parametre est retire de l'adresse : un rechargement ne rejoue rien.
 */
export function ArriveePorte({
  porte,
  geste,
}: {
  /** L'univers d'arrivee, decrit comme une porte (couleurs et sceau). */
  porte: PorteUnivers;
  /** Le parametre brut `?porte=`, deja lu par le serveur. */
  geste: string;
}) {
  const [phase, setPhase] = useState<'voile' | 'ouverture' | 'fini'>('voile');
  const point = lireGestePorte(geste);

  useEffect(() => {
    // L'adresse redevient propre, sans recharger.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('porte');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    } catch {
      // Adresse illisible : on laisse le parametre, il ne rejouera rien de plus
      // qu'un voile de 1,4 s au prochain rechargement.
    }
    if (mouvementReduit()) {
      setPhase('fini');
      return;
    }
    setPhase('ouverture');
    const fin = window.setTimeout(() => setPhase('fini'), 1000);
    return () => window.clearTimeout(fin);
  }, []);

  if (phase === 'fini') return null;
  if (phase === 'voile') return <div className={s.voileArrivee} aria-hidden="true" />;
  const x = (point?.fx ?? 0.5) * window.innerWidth;
  const y = (point?.fy ?? 0.5) * window.innerHeight;
  return <Battants porte={porte} x={x} y={y} sens="arrivee" />;
}
